import { useState, useEffect } from 'react'
import Modal from '@mui/material/Modal'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import Autocomplete from '@mui/material/Autocomplete'
import FormHelperText from '@mui/material/FormHelperText'
import TextField from '@mui/material/TextField'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import { styled } from '@mui/material/styles'
import { useParams, useActionData, useNavigate, useSubmit, useLoaderData } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { LoaderData } from '../../../types'
import { useActivityOptions, getOption } from '../../../utils'
import { SubmitSponsorshipSchema } from './SubmitSponsorship.schema'
import type { mySponsorshipLoader } from './SubmitSponsorship.loader'
import type { submitSponsorship } from './SubmitSponsorship.action'

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  borderRadius: { xs: 0, sm: 6 },
  boxShadow: 24,
  padding: { xs: 1, sm: 3 },
  width: { xs: '100%', sm: '90%' },
  height: { xs: '100%', sm: '80%' },
  overflow: 'auto',
}

const Form = styled('form')(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  flex: '1 0 auto',
}))

export default function SubmitSponsorship() {
  const { t } = useTranslation('events')
  const navigate = useNavigate()
  const submit = useSubmit()
  const actionData = useActionData() as LoaderData<ReturnType<typeof submitSponsorship>>
  const [loading, setLoading] = useState(false)
  const { eventId } = useParams()
  if (!eventId) {
    throw new Error('eventId is required')
  }

  const sponsorship = useLoaderData() as LoaderData<ReturnType<typeof mySponsorshipLoader>>

  const activityOptions = useActivityOptions(t)

  const { control, getValues, handleSubmit } = useForm<SubmitSponsorshipSchema>({
    defaultValues: {
      type: sponsorship.id,
      activity: undefined,
      request_registration_costs: false,
      request_accommodation_costs: false,
      request_travel_costs: false,
      terms_accepted: undefined,
    },
    resolver: zodResolver(SubmitSponsorshipSchema),
  })

  useEffect(() => {
    if (actionData) {
      setLoading(false)
    }
  }, [actionData, eventId, navigate])

  const handleClose = () => {
    navigate(`/events/${eventId}`)
  }

  const onSubmitForm = (event: React.FormEvent<HTMLFormElement | HTMLButtonElement>) => {
    event.preventDefault()
    const data = getValues()

    submit(
      {
        eventId: Number(eventId),
        data,
      },
      {
        method: 'POST',
        encType: 'application/json',
        action: `/events/${eventId}/sponsorship`,
      }
    )
    setLoading(true)
  }

  return (
    <Modal open onClose={handleClose}>
      <Stack sx={style}>
        <Form onSubmit={onSubmitForm}>
          <Typography variant="h1">{t('Demande de prise en charge')}</Typography>
          <Divider sx={{ margin: '1rem 0' }} />
          <Stack direction="column">
            <Controller
              name="activity"
              control={control}
              render={({ field, fieldState }) => (
                <Autocomplete
                  onChange={(_, value) => field.onChange(value?.value ?? '')}
                  onBlur={field.onBlur}
                  defaultValue={getOption(activityOptions, null, field.value) ?? undefined}
                  options={activityOptions}
                  id="activity-autocomplete"
                  disablePortal
                  disableClearable
                  fullWidth
                  renderInput={params => (
                    <TextField
                      {...params}
                      name={field.name}
                      disabled={field.disabled}
                      label={t('Activité')}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      required
                    />
                  )}
                />
              )}
            />

            <Controller
              name="request_accommodation_costs"
              control={control}
              render={({ field, fieldState }) => (
                <FormControlLabel
                  defaultChecked={field.value}
                  control={
                    <Checkbox
                      defaultChecked={field.value}
                      onChange={(_e, checked) => {
                        field.onChange(checked)
                      }}
                      sx={
                        !!fieldState.error
                          ? {
                              color: 'error.main',
                              '&.Mui-checked': {
                                color: 'error.main',
                              },
                            }
                          : undefined
                      }
                    />
                  }
                  label={t("Je demande la prise en charge des frais d'hébergement")}
                  sx={{ display: sponsorship.slug === 'medtech' ? 'none' : 'flex' }}
                />
              )}
            />
            <Controller
              name="request_registration_costs"
              control={control}
              render={({ field, fieldState }) => (
                <FormControlLabel
                  defaultChecked={field.value}
                  control={
                    <Checkbox
                      defaultChecked={field.value}
                      onChange={(_e, checked) => {
                        field.onChange(checked)
                      }}
                      sx={
                        !!fieldState.error
                          ? {
                              color: 'error.main',
                              '&.Mui-checked': {
                                color: 'error.main',
                              },
                            }
                          : undefined
                      }
                    />
                  }
                  label={t("Je demande la prise en charge des frais d'inscription")}
                  sx={{ display: sponsorship.slug === 'medtech' ? 'none' : 'flex' }}
                />
              )}
            />
            <Controller
              name="request_travel_costs"
              control={control}
              render={({ field, fieldState }) => (
                <FormControlLabel
                  defaultChecked={field.value}
                  control={
                    <Checkbox
                      defaultChecked={field.value}
                      onChange={(_e, checked) => {
                        field.onChange(checked)
                      }}
                      sx={
                        !!fieldState.error
                          ? {
                              color: 'error.main',
                              '&.Mui-checked': {
                                color: 'error.main',
                              },
                            }
                          : undefined
                      }
                    />
                  }
                  label={t('Je demande la prise en charge des frais de transport')}
                />
              )}
            />

            <Controller
              name="terms_accepted"
              control={control}
              render={({ field, fieldState }) => (
                <Stack>
                  <FormControlLabel
                    defaultChecked={field.value}
                    control={
                      <Checkbox
                        defaultChecked={field.value}
                        onChange={(_e, checked) => {
                          field.onChange(checked)
                        }}
                        sx={
                          !!fieldState.error
                            ? {
                                color: 'error.main',
                                '&.Mui-checked': {
                                  color: 'error.main',
                                },
                              }
                            : undefined
                        }
                      />
                    }
                    label={t("J'accepte les termes et conditions suivantes") + ' :'}
                    sx={{ flex: '1 0' }}
                  />
                  <FormHelperText error={!!fieldState.error}>
                    {fieldState.error?.message}
                  </FormHelperText>
                  <ul style={{ margin: 0 }}>
                    {sponsorship.terms_and_conditions?.map((term, index) => (
                      <li>
                        <FormHelperText key={`sponsors-condition-${index}`}>{term}</FormHelperText>
                      </li>
                    ))}
                  </ul>
                </Stack>
              )}
            />
          </Stack>

          <Stack flex="1 0 auto" />
          <Stack direction="row" gap="1rem" justifyContent="space-between">
            <Button onClick={handleClose} variant="outlined">
              {t('Annuler')}
            </Button>
            <Button
              onClick={e => {
                void handleSubmit(
                  () => {
                    onSubmitForm(e)
                  },
                  e => {
                    console.error(e)
                    toast.error(t('Veuillez corriger les erreurs'))
                  }
                )()
              }}
              variant="contained"
              loading={loading}
            >
              {t('Soumettre la prise en charge')}
            </Button>
          </Stack>
        </Form>
      </Stack>
    </Modal>
  )
}
